/**
 * Helper condivisi fra wc-bergamo-ce-upload e wc-bergamo-ec-upload.
 *
 * Niente classi base intermedie (Rule of Three): solo funzioni pure
 * o wrapper che operano sull'host AppComponent.
 */
import { templateParseToElement } from '../../../lib/template-parse.ts';
import type { AccessoryInputSpec } from '@/types/casse/upload-ui-spec.ts';

/** Esito singolo file caricato (archivio o accessorio). */
export interface UploadOutcome {
  ok: boolean;
  filename: string;
}

/** Stato cumulativo emesso al wizard via evento `cassa-upload-changed`. */
export interface CassaUploadState {
  archivio: UploadOutcome;
  accessori: Record<string, UploadOutcome>;
}

/** Mappa chiave logica spec -> valore `ruolo` accettato dall'API upload. */
export function accessoryKeyToRuolo(key: string): string {
  return key
    .replace(/([A-Z])/g, '_$1')
    .toLowerCase()
    .replace(/^_/, '');
}

export function buildUploadUrl(
  codice: string,
  anno: string,
  mese: string,
  ruolo: string,
): string {
  const qs = new URLSearchParams({ cassa: codice, anno, mese, ruolo });
  return `/api/elaborazioni/upload?${qs.toString()}`;
}

interface DropzoneRenderOptions {
  url: string;
  accept: string;
  maxFiles?: number;
  send?: 'auto' | 'queue';
  showUploadBtn?: boolean;
}

/** Renderizza il template `elaborazioni-dropzone` nel container indicato. */
export function renderDropzone(
  container: HTMLElement,
  opts: DropzoneRenderOptions,
): HTMLElement | null {
  templateParseToElement('elaborazioni-dropzone', container, {
    url: opts.url,
    accept: opts.accept,
    maxFiles: opts.maxFiles ?? 1,
    send: opts.send ?? 'auto',
    showUploadBtn: opts.showUploadBtn ?? true,
  });
  return container.querySelector('[data-dropzone]') as HTMLElement | null;
}

/**
 * Aggancia gli handler `uploaded`/`error` al dropzone di un accessorio sindacale
 * (singolo file). Aggiorna lo stato e invoca onChange.
 */
export function bindAccessorioEvents(
  el: HTMLElement,
  key: string,
  state: CassaUploadState,
  onChange: () => void,
): void {
  el.addEventListener('uploaded.wx.dropzone', (e: Event) => {
    const detail = (e as CustomEvent).detail as { response?: { filename?: string } } | undefined;
    state.accessori[key] = {
      ok: true,
      filename: detail?.response?.filename ?? '',
    };
    onChange();
  });

  el.addEventListener('error.wx.dropzone', () => {
    state.accessori[key] = { ok: false, filename: '' };
    onChange();
  });
}

/**
 * Costruisce lo stato iniziale (vuoto) per gli accessori dichiarati nella spec.
 */
export function initAccessoriState(
  accessoryInputs: Record<string, AccessoryInputSpec> | undefined,
): Record<string, UploadOutcome> {
  const out: Record<string, UploadOutcome> = {};
  if (!accessoryInputs) return out;
  for (const key of Object.keys(accessoryInputs)) {
    out[key] = { ok: false, filename: '' };
  }
  return out;
}
